import { InboxIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { Column, DataTable } from '../../components/ui/DataTable';
import { Money } from '../../components/ui/Money';
import { PaymentChip, StatusChip } from '../../components/ui/StatusChip';
import { ADVANCE_LABEL_KEY } from '../../components/ui/status';
import type { Order, OrderStatus } from '../../types/Order';
import { LATE_AFTER_MIN, WARN_AFTER_MIN } from './config';
import { PaymentChange } from './orderApi';
import { orderMenu } from './orderMenu';
import { OrderPlace } from './OrderTicket';
import { formatElapsed, liveSeconds, minutesSince } from './time';

interface Props {
    orders: Order[];
    now: number;
    saving: ReadonlySet<string>;
    /** true when a filter hides every order that exists */
    filtered: boolean;
    showLocation: boolean;
    onAdvance: (order: Order) => void;
    onPay: (order: Order, p: PaymentChange) => void;
    onMove: (order: Order, to: OrderStatus) => void;
    onCancel: (order: Order) => void;
}

/** The board's orders as a list: the kit DataTable (a card per order on a phone), oldest first. */
export const OrderTable = ({ orders, now, saving, filtered, showLocation, onAdvance, onPay, onMove, onCancel }: Props) => {
    const { t } = useTranslation(['orders', 'common']);

    const columns: Column<Order>[] = [
        {
            id: 'number',
            header: t('table.order'),
            priority: 'primary',
            cell: (o) => `#${o.orderNumber}`,
            sortValue: (o) => Date.parse(o.createdAt),
        },
        {
            id: 'place',
            header: t('table.place'),
            priority: 'primary',
            cell: (o) => (
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-small font-normal text-body">
                    {showLocation && o.locationName && <span className="font-semibold text-strong">{o.locationName}</span>}
                    <OrderPlace order={o} />
                </span>
            ),
            sortValue: (o) => (o.orderType === 'togo' ? '' : (o.tableNumber ?? '')),
        },
        {
            id: 'items',
            header: t('table.items'),
            priority: 'secondary',
            cell: (o) => {
                const text = o.orderItems.map((i) => `${i.quantity}× ${i.name}`).join(', ');
                return (
                    <span className="block max-w-[28ch] truncate max-lg:max-w-none max-lg:whitespace-normal" title={text}>
                        {text}
                    </span>
                );
            },
        },
        {
            id: 'total',
            header: t('table.total'),
            priority: 'meta',
            align: 'end',
            cell: (o) => <Money amount={o.totalAmount} symbol={o.currencySymbol} position={o.currencySymbolPosition} />,
            sortValue: (o) => Number(o.totalAmount),
        },
        {
            id: 'status',
            header: t('table.status'),
            priority: 'meta',
            cell: (o) => <StatusChip status={o.status} />,
        },
        {
            id: 'payment',
            header: t('table.payment'),
            priority: 'meta',
            cell: (o) => <PaymentChip status={o.paymentStatus} method={o.paymentMethod} orderStatus={o.status} />,
        },
        {
            id: 'age',
            header: t('table.age'),
            priority: 'meta',
            cell: (o) => {
                const minutes = minutesSince(o.createdAt, now);
                const watched = o.status === 'pending' || o.status === 'accepted';
                const inState = Math.floor((liveSeconds(o, now)[o.status as 'pending'] ?? 0) / 60);
                const approx = o.timing.estimated ? '~' : '';
                return (
                    <span className="tabular-nums">
                        <span
                            className={clsx(
                                'block whitespace-nowrap',
                                watched && minutes >= LATE_AFTER_MIN ? 'font-semibold text-danger' : watched && minutes >= WARN_AFTER_MIN ? 'font-semibold text-tone-warning-fg' : 'text-strong'
                            )}
                        >
                            {approx}
                            {formatElapsed(inState, t)}
                        </span>
                        <span className="block whitespace-nowrap text-caption text-muted">{t('ticket.total', { time: `${approx}${formatElapsed(minutes, t)}` })}</span>
                    </span>
                );
            },
            sortValue: (o) => Date.parse(o.createdAt),
        },
        {
            id: 'advance',
            header: t('table.next'),
            priority: 'secondary',
            cell: (o) => {
                const key = ADVANCE_LABEL_KEY[o.status];
                return key ? (
                    <Button size="md" variant={o.status === 'pending' ? 'primary' : 'strong'} disabled={saving.has(o.id)} onClick={() => onAdvance(o)}>
                        {t(key, { ns: 'common' })}
                    </Button>
                ) : null;
            },
        },
    ];

    return (
        <DataTable
            rows={orders}
            columns={columns}
            getRowId={(o) => o.id}
            rowHref={(o) => `/orders/${o.id}`}
            rowActions={(o) => orderMenu(t, o, { onPay, onMove, onCancel })}
            rowLabel={(o) => `#${o.orderNumber}`}
            state="ready"
            caption={t('table.caption')}
            tableFrom="lg"
            empty={{
                icon: InboxIcon,
                title: filtered ? t('table.noMatch') : t('table.empty'),
                body: filtered ? t('table.noMatchBody') : t('table.emptyBody'),
            }}
        />
    );
};
